import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback } from 'react';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { RouteButton } from '@/components/RouteButton/RouteButton';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useIsMobileLayout } from '@/hooks';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummaryPromoted } from '@/redux/modules/item/summary/itemSummary.selectors';
import { BuyersPremium } from '@/types/BuyersPremium';
import { WonItemInfo } from '@/types/WonItem';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';
import { calculateBuyersPremium } from '@/utils/calculateBuyersPremium';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getImageUrl, getItemUrlFromSlug, getSimilarItemsUrl } from '@/utils/urls';

export type Props = {
    buyersPremium?: BuyersPremium;
    item: WonItemInfo;
    won: boolean;
};

export const WonItemRow = ({ buyersPremium, item, won }: Props) => {
    const formatMessage = useAppFormatMessage();
    const isMobile = useIsMobileLayout();
    const trackWonItemCardClicked = useTrackItemCardClicked();

    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, item.itemId));

    const calculateItemTotal = (hammerPrice: number) => {
        if (!buyersPremium) {
            return hammerPrice;
        }
        const { amount: premiumAmount } = calculateBuyersPremium(hammerPrice, buyersPremium);
        return hammerPrice + premiumAmount;
    };

    const { catalogId, currency = 'USD', hammerPrice, itemId, lotNumber, sellerId, slugWithLocation, title } = item;
    const itemHref = getItemUrlFromSlug(itemId, slugWithLocation);
    const similarItemUrl = getSimilarItemsUrl(title);
    //TODO: we need to replace this "imageNumber: 1" this is not necessarily the first image
    const imageUrl = getImageUrl({
        catalogId,
        imageNumber: 1,
        itemId,
        sellerId,
        width: 150,
    });

    const handleItemClick = useCallback(() => {
        trackWonItemCardClicked({
            catalogId,
            itemId,
            promoted,
            source: ItemCardSource.WonItemsPage,
        });
    }, [catalogId, itemId, promoted, trackWonItemCardClicked]);

    return (
        <div className="mb-lg flex last:mb-0">
            <div className="flex flex-auto">
                <div
                    className={cn(
                        'mr-sm shrink-0 border-2 border-solid border-border-primary',
                        isMobile ? 'h-[76px] w-[76px]' : 'h-[96px] w-[96px]'
                    )}
                >
                    <RouteLink
                        className={cn(
                            'flex items-center justify-center',
                            isMobile ? 'h-[72px] w-[72px]' : 'h-[92px] w-[92px]'
                        )}
                        onClick={handleItemClick}
                        to={itemHref}
                    >
                        <img
                            alt={title}
                            className="max-h-full max-w-full object-contain"
                            src={imageUrl}
                        />
                    </RouteLink>
                </div>
                <div className="mb-xs">
                    <RouteLink
                        className="mb-xs text-text-primary no-underline"
                        onClick={handleItemClick}
                        to={itemHref}
                    >
                        <BodyPrimary>{title}</BodyPrimary>
                    </RouteLink>
                    {lotNumber && (
                        <BodySecondary
                            className="block"
                            color="secondary"
                        >
                            {formatMessage(['item_page_lot_number'], {
                                values: { number: lotNumber },
                            })}
                        </BodySecondary>
                    )}
                    {hammerPrice > 0 && (
                        <BodySecondary
                            className="block"
                            color="secondary"
                        >
                            <FormattedCurrency
                                sourceCurrency={currency}
                                value={calculateItemTotal(hammerPrice)}
                            />
                        </BodySecondary>
                    )}
                </div>
            </div>
            {!won && (
                <div className={cn('flex w-[50%] justify-end', isMobile ? 'block' : '')}>
                    <div>
                        <RouteButton to={similarItemUrl}>{formatMessage(['view_similar_items'])}</RouteButton>
                    </div>
                </div>
            )}
        </div>
    );
};
